import { fetchEventSource, type EventSourceMessage } from '@microsoft/fetch-event-source';
import apiClient from '../../../services/apiClient';
import eventBus from '../../../services/globalEventBus';
import { getJwt } from '../../../services/jwt';
import { logger } from '../../../utils/logger';

const PING_TIMEOUT_MS = 45000;
const PONG_MAX_RETRY_COUNT = 0;
const MAX_RECEIVED_EVENTS = 1000;
const SSE_CONTENT_TYPE = 'text/event-stream';

class FatalEventStreamError extends Error {
  constructor(status: number) {
    super(`SSE request failed with status ${status}`);
    this.name = 'FatalEventStreamError';
  }
}

function clearTimer(timer: ReturnType<typeof setTimeout> | null): null {
  if (timer !== null) {
    clearTimeout(timer);
  }
  return null;
}

function sseHeaders(token: string): Record<string, string> {
  return {
    Accept: SSE_CONTENT_TYPE,
    'Accept-Encoding': 'identity',
    Authorization: token,
    'Cache-Control': 'no-cache',
  };
}

function publishToEventBus(eventType: string, event: unknown): void {
  try {
    eventBus.publish(eventType, event);
  } catch (error) {
    logger.error(`[EventStreamManager] Publishing to global eventBus failed for event: ${eventType}`, error);
  }
}

class EventStreamClient {
  private isOpen = false;
  private abortController: AbortController | undefined;
  private activeUserId: string | undefined;
  private failedCreations = 0;
  private pingWatchdog: ReturnType<typeof setTimeout> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private receivedEvents = new Set<string>();
  /** Softens disconnect logs across a background → foreground recovery cycle. */
  private recoverQuietly = false;
  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      // Timers coalesce on wake and look like a ping timeout even when the
      // stream was only suspended. Pause the watchdog while the tab is hidden.
      this.pingWatchdog = clearTimer(this.pingWatchdog);
      return;
    }

    if (this.isOpen && this.activeUserId) {
      this.schedulePingWatchdog(this.activeUserId);
    }
  };

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  }

  connect(userId: string): void {
    if (!userId) {
      return;
    }

    if (this.abortController) {
      if (this.activeUserId === userId) {
        return;
      }
      this.reset();
    }

    this.open(userId);
  }

  shutdown(): void {
    this.reset();
  }

  isConnected(): boolean {
    return this.isOpen;
  }

  getActiveUserId(): string | undefined {
    return this.activeUserId;
  }

  private open(userId: string): void {
    const token = getJwt();
    if (!token) {
      logger.warn('[EventStreamManager] SSE connection requires an authenticated user');
      return;
    }

    logger.info('[EventStreamManager] Creating SSE connection', { userId });

    const controller = new AbortController();
    this.abortController = controller;
    this.activeUserId = userId;
    this.isOpen = false;
    this.reconnectTimer = clearTimer(this.reconnectTimer);

    fetchEventSource(`${window.getApiUrl()}/v1/users/${userId}/events`, {
      credentials: 'include',
      headers: sseHeaders(token),
      openWhenHidden: true,
      signal: controller.signal,
      onopen: response =>
        Promise.resolve().then(() => {
          this.validateOpenResponse(response);
          this.isOpen = true;
          this.recoverQuietly = false;
          this.schedulePingWatchdog(userId);
          publishToEventBus('SOCKET_CONNECTED', {});
        }),
      onmessage: message => {
        this.handleMessage(userId, message);
      },
      onclose: () => {
        this.pingWatchdog = clearTimer(this.pingWatchdog);
        throw new Error('SSE connection closed');
      },
      onerror: error => {
        if (!(error instanceof FatalEventStreamError)) {
          logger.warn('[EventStreamManager] SSE connection failed', error);
        }
        throw error;
      },
    }).catch(error => this.handleConnectionError(userId, error, controller.signal));
  }

  private validateOpenResponse(response: Response): void {
    const contentType = response.headers.get('content-type');

    if (!response.ok || !contentType?.startsWith(SSE_CONTENT_TYPE)) {
      if (response.status === 401 || response.status === 403) {
        throw new FatalEventStreamError(response.status);
      }
      throw new Error(`Unexpected SSE response: ${response.status}`);
    }
  }

  private handleMessage(userId: string, message: EventSourceMessage): void {
    this.schedulePingWatchdog(userId);

    if (message.event.toLowerCase() === 'ping') {
      this.failedCreations = 0;
      this.sendPong(userId, message.data);
      return;
    }

    if (message.event === 'ApiError') {
      this.handleApiError(message.data);
      return;
    }

    this.handleDomainMessage(message.data);
  }

  private sendPong(userId: string, data: string): void {
    try {
      const { clientId } = JSON.parse(data) as { clientId: string };
      apiClient
        .post(
          `/v1/users/${userId}/events/pong?clientId=${clientId}`,
          {},
          {
            headers: { 'Content-Type': 'application/json' },
          },
          [],
          PONG_MAX_RETRY_COUNT
        )
        .catch(error => logger.error('[EventStreamManager] Failed to send pong', error));
    } catch (error) {
      logger.error('[EventStreamManager] Failed to parse ping data', error);
    }
  }

  private handleApiError(data: string): void {
    try {
      const json = JSON.parse(data);
      logger.debug(`[EventStreamManager] ApiError: ${json.code} ${data}`);
      publishToEventBus(json.code, json);
    } catch (error) {
      logger.error('[EventStreamManager] Failed to parse ApiError', error);
    }
  }

  private handleDomainMessage(data: string): void {
    try {
      const json = JSON.parse(data);
      if (json.uniqueId && this.receivedEvents.has(json.uniqueId)) {
        logger.debug(`[EventStreamManager] Duplicate event received, not broadcasting: ${json.eventType} ${data}`);
        return;
      }

      if (json.uniqueId) {
        this.receivedEvents.add(json.uniqueId);
        if (this.receivedEvents.size > MAX_RECEIVED_EVENTS) {
          const oldest = this.receivedEvents.values().next().value;
          if (oldest !== undefined) {
            this.receivedEvents.delete(oldest);
          }
        }
      }

      logger.debug(`[EventStreamManager] Message: ${json.eventType} ${data}`);
      publishToEventBus(json.eventType, json);
    } catch (error) {
      logger.error('[EventStreamManager] Failed to parse message', error);
    }
  }

  private schedulePingWatchdog(userId: string): void {
    this.pingWatchdog = clearTimer(this.pingWatchdog);
    if (document.hidden) {
      return;
    }

    this.pingWatchdog = setTimeout(() => {
      this.logDisconnect('[EventStreamManager] PING TIMEOUT');
      this.recover(userId);
    }, PING_TIMEOUT_MS);
  }

  private handleConnectionError(userId: string, error: unknown, signal: AbortSignal): void {
    if (signal.aborted) {
      return;
    }

    if (error instanceof FatalEventStreamError) {
      logger.error('[EventStreamManager] SSE authentication failed', error);
      this.shutdown();
      return;
    }

    this.logDisconnect('[EventStreamManager] SSE connection stopped', error);
    this.recover(userId);
  }

  private recover(userId: string): void {
    if (document.hidden) {
      this.recoverQuietly = true;
    }

    this.teardown();
    this.failedCreations++;

    const delayMs = 1000 * Math.pow(this.failedCreations, 2);
    this.logDisconnect('[EventStreamManager] Socket disconnected', { userId, delayMs });

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.abortController) {
        return;
      }
      this.open(userId);
    }, delayMs);

    publishToEventBus('SOCKET_DISCONNECTED', { userId });
  }

  private logDisconnect(message: string, details?: unknown): void {
    if (document.hidden || this.recoverQuietly) {
      if (details === undefined) {
        logger.warn(message);
      } else {
        logger.warn(message, details);
      }
      return;
    }

    if (details === undefined) {
      logger.error(message);
    } else {
      logger.error(message, details);
    }
  }

  private reset(): void {
    this.teardown();
    this.failedCreations = 0;
    this.recoverQuietly = false;
    this.receivedEvents.clear();
  }

  private teardown(): void {
    this.pingWatchdog = clearTimer(this.pingWatchdog);
    this.reconnectTimer = clearTimer(this.reconnectTimer);

    if (this.abortController) {
      this.abortController.abort();
      this.abortController = undefined;
    }

    this.isOpen = false;
    this.activeUserId = undefined;
  }
}

const client = new EventStreamClient();

export function connectEventStream(streamId: string): void {
  client.connect(streamId);
}

export function shutdownEventStream(): void {
  client.shutdown();
}

export function isEventStreamConnected(): boolean {
  return client.isConnected();
}

export function getActiveStreamUserId(): string | undefined {
  return client.getActiveUserId();
}

export function subscribeToEventStreamLifecycle(): () => void {
  const unsubscribers = [
    eventBus.subscribe('NoUserFound', () => shutdownEventStream()),
    eventBus.subscribe('unauthorized', () => shutdownEventStream()),
  ];

  return () => {
    unsubscribers.forEach(unsubscribe => unsubscribe());
  };
}
